import React, { useCallback, useContext } from "react";
import {
  BlockStack,
  Button,
  Text,
  ChoiceList,
  TextField,
  Select,
  Banner,
} from "@shopify/polaris";
import { ResourcePicker } from "@shopify/app-bridge/actions";
import { AppBridgeContext } from "../AppBridgeProvider";
import { ResourcePickerBlock } from "./ResourcePickerList";

const UPSELL_MODES = [
  { label: "No upsell", value: "none" },
  { label: "Frequently bought together (Shopify recommendations)", value: "frequently_bought" },
  { label: "Specific products", value: "specific" },
];

const CTA_ACTIONS = [
  { label: "Add to Cart", value: "add_to_cart" },
  { label: "Go to product", value: "go_to_product" },
];

const CTA_DEFAULT_BUTTON_TEXT = {
  add_to_cart: "Add to cart",
  go_to_product: "View product",
};

function numericId(gid) {
  if (!gid) return null;
  var m = String(gid).match(/(\d+)$/);
  return m ? m[1] : gid;
}

function mergeById(existing, incoming) {
  const map = new Map();
  (existing || []).forEach((item) => {
    if (item?.id != null) map.set(String(item.id), item);
  });
  (incoming || []).forEach((item) => {
    if (item?.id == null) return;
    const prev = map.get(String(item.id));
    map.set(String(item.id), prev ? { ...prev, ...item } : item);
  });
  return Array.from(map.values());
}

/** Map ResourcePicker selection -> storefront-ready product info (title, image, price, variant). */
function mapSelection(selection) {
  return (selection || []).map(function (s) {
    var firstVariant = (s.variants && s.variants[0]) || {};
    var firstImage = (s.images && s.images[0]) || {};
    return {
      id: numericId(s.id),
      handle: s.handle,
      title: s.title,
      image: firstImage.originalSrc || firstImage.src || s.image || null,
      variant_id: numericId(firstVariant.id) || null,
      price: firstVariant.price != null ? Math.round(Number(firstVariant.price) * 100) : null,
    };
  });
}

export default function UpsellSection({ upsell, onPatch }) {
  const app = useContext(AppBridgeContext);
  const u = upsell || {};
  const mode = u.mode || "none";
  const products = Array.isArray(u.products) ? u.products : [];
  const cta = u.cta || {};

  const runPicker = useCallback(() => {
    if (!app) return;
    try {
      const initialSelectionIds = products.map((p) => ({
        id: String(p.id).startsWith("gid://") ? p.id : `gid://shopify/Product/${p.id}`,
      }));
      const picker = ResourcePicker.create(app, {
        resourceType: ResourcePicker.ResourceType.Product,
        options: { selectMultiple: true, showVariants: false, initialSelectionIds },
      });
      picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
        onPatch({ products: mergeById(products, mapSelection(payload.selection || [])) });
        picker.unsubscribe();
      });
      picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
      picker.dispatch(ResourcePicker.Action.OPEN);
    } catch (e) {
      console.error("[Upsell] product picker", e);
    }
  }, [app, onPatch, products]);

  const removeProduct = useCallback(
    (id) => {
      onPatch({ products: products.filter((p) => String(p.id) !== String(id)) });
    },
    [onPatch, products],
  );

  return (
    <BlockStack gap="400">
      <ChoiceList
        title="Select upsell products"
        choices={UPSELL_MODES}
        selected={[mode]}
        onChange={(v) => onPatch({ mode: v[0] || "none" })}
      />

      {mode === "specific" ? (
        <BlockStack gap="200">
          {!app ? (
            <Banner tone="warning" title="Resource picker needs Shopify Admin">
              Open this page from Shopify admin to pick products.
            </Banner>
          ) : null}
          <ResourcePickerBlock
            buttonLabel="Select products"
            onPick={runPicker}
            items={products}
            onRemove={removeProduct}
            emptyLabel="No products selected."
          />
        </BlockStack>
      ) : null}

      {mode !== "none" ? (
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Call to action settings
          </Text>
          <Select
            label="Call to action"
            options={CTA_ACTIONS}
            value={cta.action || "add_to_cart"}
            onChange={(v) =>
              onPatch({
                cta: {
                  ...cta,
                  action: v,
                  button_text: CTA_DEFAULT_BUTTON_TEXT[v] || cta.button_text,
                },
              })
            }
          />
          <TextField
            label="Button text"
            value={cta.button_text || CTA_DEFAULT_BUTTON_TEXT[cta.action || "add_to_cart"] || "Add to cart"}
            onChange={(v) => onPatch({ cta: { ...cta, button_text: v } })}
            autoComplete="off"
          />
        </BlockStack>
      ) : null}
    </BlockStack>
  );
}
